import {
  handleUpdateStudySetting,
  PRIMARY_STUDY_TYPE_LIST,
  STUDY_TYPES,
  STUDY_STATUS,
  FOLLOW_ON_STUDY_TYPE,
  checkStudyDuration,
  PENDING_COMMAND_EXISTS_MESSAGE,
  checkUsingHighSamplingRateStudy,
} from '@/entities/study';
import { checkIcorDevice } from '@/entities/device';
import { DEVICE, VERSION_CAN_RUN_MCT } from '@/shared/config';
import {
  showMessageError,
  showMessageInfo,
  showMessageSuccess,
  addTimezoneToMoment,
  adjustStudyDate,
  convertToMoment,
} from '@/shared/lib';
import { useSetState } from '@/shared/lib/customHooks';
import dayjs from 'dayjs';
import _ from 'lodash';
import { useEffect, useImperativeHandle, useMemo, useRef } from 'react';
import { t } from 'ttag';
import { convertPayloadStudySetting } from '../../../../lib/studyDetailsTab';
import {
  NON_NUMBER_STUDY_TYPE_OPTIONS,
  STUDY_OPTIONS,
  VIRTU_OX_NON_NUMBER_STUDY_TYPE_OPTIONS,
} from '../../../../config/editStudyDetails';
import type { EditStudyInfoDrawerState, EditStudyInfoProps } from '../editStudyDetails.types';
import { getTimeDuration, getUnitTime } from '../../../../lib/editStudyDetails';

interface StudyInfoStoreData {
  duration: any;
  unitTime: any;
  studyType: any;
  primaryStudyType: any;
  followOnStudyStudyType: any;
  followOnStudyDuration: any;
  unitTimeFO: any;
  reportDueDate: any;
  followOnStudyReportDueDate: any;
  pendingCommand: any;
  ringId: any;
}

/**
 * Drives the edit-study-info drawer covering study type, duration, ring ID and the patient return visit dates. Seeds state from the study whenever the drawer opens, derives the ongoing, draft, completed, Holter and follow-on flags plus the read-only condition and the study type options, warns when the return visit falls within 3 business days of the study end, and exposes the change, save-guard, save and close handlers plus a handleCloseModals imperative handle.
 *
 * @param data - Study whose information is being edited.
 * @param open - Whether the drawer is open, which triggers the state seeding.
 * @param updateStudyDetail - Callback refreshing the study detail after a successful save.
 * @param adjustedStudyDayOffset - Day offset applied to the study end when computing the return visit warning limit.
 * @param hasPendingCommandMessage - Whether a pending command message blocks editing.
 * @param onClose - Callback closing the drawer identified by its key.
 * @param drawerKey - Key identifying this drawer to the close callback.
 * @param isStopping - Whether the study is being stopped, which blocks editing.
 * @param ref - Forwarded ref exposing handleCloseModals to the parent.
 * @returns Form state, derived study flags and option lists, and the change, save and close handlers.
 */
export const useEditStudyInfoDrawer = (
  {
    data = {},
    open,
    updateStudyDetail,
    adjustedStudyDayOffset,
    hasPendingCommandMessage,
    onClose,
    drawerKey,
    isStopping,
  }: EditStudyInfoProps,
  ref: any,
) => {
  const [state, setState] = useSetState<EditStudyInfoDrawerState>({
    studyType: null,
    primaryStudyType: null,
    duration: undefined,
    followOnStudyStudyType: undefined,
    followOnStudyDuration: undefined,
    listError: {},
    isOpenConfirmModal: false,
    isSaving: false,
    unitTime: undefined,
    unitTimeFO: undefined,
    ringId: '',
    isInitiating: false,
  });

  const storeData = useRef<StudyInfoStoreData>({
    duration: undefined,
    unitTime: undefined,
    studyType: undefined,
    primaryStudyType: undefined,
    followOnStudyStudyType: undefined,
    followOnStudyDuration: undefined,
    unitTimeFO: undefined,
    reportDueDate: undefined,
    followOnStudyReportDueDate: undefined,
    pendingCommand: undefined,
    ringId: undefined,
  });

  const hasDevicePendingCommand = !_.isEmpty(data.pendingCommand);
  const studyOngoing = data.status === STUDY_STATUS.ONGOING;
  const draftOrStartingStudy = [STUDY_STATUS.DRAFT, STUDY_STATUS.STARTING].includes(data.status);

  /**
   * Indicates whether the study has passed its scheduled stop time. Compares the current time against data.stop with dayjs.
   *
   * @returns True when the study duration has elapsed.
   */
  const isEndOfDurationStudy = useMemo(() => dayjs().isAfter(dayjs(data?.stop)), [data]);

  /**
   * Indicates whether an iCOR study has already finished. Resolves to true only for iCOR devices whose status is completed or done, or whose duration has elapsed; any other device yields false.
   *
   * @returns True when the iCOR study is finished.
   */
  const isCompletedIcorStudy = useMemo(() => {
    const isIcorDevice = checkIcorDevice(data.device);
    if (isIcorDevice) {
      return (
        [STUDY_STATUS.COMPLETED, STUDY_STATUS.DONE].includes(data.status) || isEndOfDurationStudy
      );
    }
    return false;
  }, [data?.device, data?.status, isEndOfDurationStudy]);

  /**
   * Indicates whether the study has finished and its information is no longer live. True when the status is completed or done, or when the iCOR-specific completion check passes.
   *
   * @returns True for a completed study.
   */
  const isCompletedStudy =
    [STUDY_STATUS.COMPLETED, STUDY_STATUS.DONE].includes(data.status) || isCompletedIcorStudy;

  const isFollowOnStudyType = state.studyType === FOLLOW_ON_STUDY_TYPE;

  /**
   * Indicates whether the primary recording is a Holter study. True when the selected study type is Holter, or when a follow-on study is selected whose primary study type is Holter.
   *
   * @returns True for a Holter primary study.
   */
  const isFirstHolterStudy =
    state.studyType === STUDY_TYPES.HOLTER ||
    (isFollowOnStudyType && state.primaryStudyType === STUDY_TYPES.HOLTER);

  const isSecondHolterStudy = state.followOnStudyStudyType === STUDY_TYPES.HOLTER;

  /**
   * Reports whether the study information form differs from the values the drawer was opened with. Deep-compares the study type, duration and report due date plus their follow-on counterparts between state and storeData.current.
   *
   * @returns True when at least one tracked field was edited.
   */
  const checkIsEditting = () => {
    const dataToCompare = _.pick(_.cloneDeep(state), [
      'studyType',
      'primaryStudyType',
      'duration',
      'reportDueDate',
      'followOnStudyStudyType',
      'followOnStudyDuration',
      'followOnStudyReportDueDate',
    ]);
    const previousDataToCompare = _.pick(_.cloneDeep(storeData.current), [
      'studyType',
      'primaryStudyType',
      'duration',
      'reportDueDate',
      'followOnStudyStudyType',
      'followOnStudyDuration',
      'followOnStudyReportDueDate',
    ]);
    return !_.isEqual(dataToCompare, previousDataToCompare);
  };

  /**
   * Builds the warning shown when the patient return visit falls too close to the end of the study. Computes a limit of 3 business days after the timezone-adjusted study end, including the follow-on duration for unfinished studies, and returns the End of Use report warning text when the chosen return visit is earlier than that limit, otherwise an empty string.
   *
   * @returns The warning message, or an empty string.
   */
  const reportDueDateMessage = useMemo(() => {
    if (!state.reportDueDate || !data.stop) {
      return '';
    }

    const hasCompletedStudy = [STUDY_STATUS.COMPLETED, STUDY_STATUS.DONE].includes(data.status);
    const limitMoment = addTimezoneToMoment({
      dateMoment: adjustStudyDate({
        dateMoment: dayjs(data.start),
        adjustedStudyDayOffset: adjustedStudyDayOffset ?? 0,
      })
        .add(
          !hasCompletedStudy && !isEndOfDurationStudy
            ? Number(state.duration || 0) + Number(state.followOnStudyDuration || 0)
            : Number(state.duration || 0),
          'days',
        )
        .endOf('day'),
    });
    const limit = limitMoment ? limitMoment.businessDaysAdd(3).valueOf() : 0;
    const isShowReportDueDateMessage =
      !hasCompletedStudy && !isEndOfDurationStudy
        ? state.followOnStudyReportDueDate
          ? (addTimezoneToMoment({
              dateMoment: dayjs(state.followOnStudyReportDueDate).endOf('day'),
            })?.valueOf() ?? 0) < limit
          : state.reportDueDate
            ? (addTimezoneToMoment({
                dateMoment: dayjs(state.reportDueDate).endOf('day'),
              })?.valueOf() ?? 0) < limit
            : ''
        : state.reportDueDate
          ? (addTimezoneToMoment({
              dateMoment: dayjs(state.reportDueDate).endOf('day'),
            })?.valueOf() ?? 0) < limit
          : '';
    return isShowReportDueDateMessage && (state.followOnStudyReportDueDate || state.reportDueDate)
      ? t`The End of Use report may not be available at this time. The patient return visit date is within 3 business days of the end of the study.`
      : '';
  }, [data, state.reportDueDate, state.followOnStudyReportDueDate]);

  /**
   * Indicates whether the study information fields must stay read-only. Memoized as true for test studies, a pending command message, a study that is not ongoing, a device pending command, a stopping study, or a completed iCOR study.
   *
   * @returns True when editing is blocked.
   */
  const isDisabledEdit = useMemo(
    () =>
      data.isTest ||
      hasPendingCommandMessage ||
      !studyOngoing ||
      hasDevicePendingCommand ||
      isStopping ||
      isCompletedIcorStudy,
    [
      data.isTest,
      hasPendingCommandMessage,
      studyOngoing,
      hasDevicePendingCommand,
      isStopping,
      isCompletedIcorStudy,
    ],
  );

  /**
   * Applies a study information field change and recomputes the dependent patient return visit dates. Duration and study type derive new report due dates by adding 3 or 4 business days for a follow-on study and 5 or 6 otherwise after the new stop date, keeping the stored date when it is already later; the unit selects reset the duration to 50 hours or 3 days, the follow-on fields recompute only the follow-on due date, and any other field is written straight to state.
   *
   * @param name - Name of the form field that changed.
   * @param value - New value for that field.
   * @returns Nothing.
   */
  const handleChange = (name: string, value: string) => {
    if (name === 'duration') {
      const setStateObject = {};

      if (value) {
        const newStopMomentBusiness = dayjs(data?.start).add(Number(value), 'day');
        const newFollowOnStopMomentBusiness = dayjs(data?.start).add(
          Number(value) + Number(state.followOnStudyDuration || 0),
          'day',
        );

        if (state.studyType === FOLLOW_ON_STUDY_TYPE) {
          let newReportDueDateMomentBusiness = newStopMomentBusiness.businessDaysAdd(
            newStopMomentBusiness.add(1, 'days').isBusinessDay() ? 3 : 4,
          );
          if (
            storeData.current.reportDueDate &&
            newReportDueDateMomentBusiness.valueOf() > storeData.current.reportDueDate.valueOf()
          ) {
            // addTimezoneToMoment only returns null for an invalid moment; newReportDueDateMomentBusiness is always a valid moment built from businessDaysAdd.
            newReportDueDateMomentBusiness = addTimezoneToMoment({
              dateMoment: newReportDueDateMomentBusiness,
            })!.startOf('days');
            _.assign(setStateObject, { reportDueDate: newReportDueDateMomentBusiness });
          } else {
            _.assign(setStateObject, {
              reportDueDate: storeData.current.reportDueDate
                ? convertToMoment({ isoDate: storeData.current.reportDueDate })
                : undefined,
            });
          }

          let newFollowOnStudyReportDueDateMomentBusiness;
          newFollowOnStudyReportDueDateMomentBusiness =
            newFollowOnStopMomentBusiness.businessDaysAdd(
              newFollowOnStopMomentBusiness.add(1, 'days').isBusinessDay() ? 5 : 6,
            );
          if (
            newFollowOnStudyReportDueDateMomentBusiness.valueOf() >
            storeData.current.followOnStudyReportDueDate?.valueOf()
          ) {
            // addTimezoneToMoment only returns null for an invalid moment; newFollowOnStudyReportDueDateMomentBusiness is always a valid moment built from businessDaysAdd.
            newFollowOnStudyReportDueDateMomentBusiness = addTimezoneToMoment({
              dateMoment: newFollowOnStudyReportDueDateMomentBusiness,
            })!.startOf('days');
            _.assign(setStateObject, {
              followOnStudyReportDueDate: newFollowOnStudyReportDueDateMomentBusiness,
            });
          } else {
            _.assign(setStateObject, {
              followOnStudyReportDueDate: storeData.current.followOnStudyReportDueDate
                ? convertToMoment({ isoDate: storeData.current.followOnStudyReportDueDate })
                : undefined,
            });
          }
        } else {
          let newReportDueDateMomentBusiness = newStopMomentBusiness.businessDaysAdd(
            newStopMomentBusiness.add(1, 'days').isBusinessDay() ? 5 : 6,
          );
          if (
            storeData.current.reportDueDate &&
            newReportDueDateMomentBusiness.valueOf() > storeData.current.reportDueDate.valueOf()
          ) {
            // addTimezoneToMoment only returns null for an invalid moment; newReportDueDateMomentBusiness is always a valid moment built from businessDaysAdd.
            newReportDueDateMomentBusiness = addTimezoneToMoment({
              dateMoment: newReportDueDateMomentBusiness,
            })!.startOf('days');
            _.assign(setStateObject, { reportDueDate: newReportDueDateMomentBusiness });
          } else {
            _.assign(setStateObject, {
              reportDueDate: storeData.current.reportDueDate
                ? convertToMoment({ isoDate: storeData.current.reportDueDate })
                : undefined,
            });
          }
        }
      }

      _.assign(setStateObject, {
        duration: value,
        listError: {},
      });

      setState(setStateObject);

      return;
    }

    if (name === 'unitTime') {
      if (value === 'hour') {
        setState({ duration: 50 });
      } else {
        setState({ duration: 3 });
      }
    }

    if (name === 'unitTimeFO') {
      if (value === 'hour') {
        setState({ followOnStudyDuration: 50 });
      } else {
        setState({ followOnStudyDuration: 3 });
      }
    }

    if (name === 'reportDueDate') {
      setState({
        reportDueDate: value || undefined,
        listError: {},
      });
      return;
    }

    if (name === 'followOnStudyDuration') {
      const setStateObject: any = {
        followOnStudyDuration: value,
        listError: {},
      };

      if (value) {
        const newFollowOnStopMomentBusiness = dayjs(data?.start).add(
          Number(state.duration || 0) + Number(value),
          'day',
        );

        let newFollowOnStudyReportDueDateMomentBusiness;
        newFollowOnStudyReportDueDateMomentBusiness = newFollowOnStopMomentBusiness.businessDaysAdd(
          newFollowOnStopMomentBusiness.add(1, 'days').isBusinessDay() ? 5 : 6,
        );
        if (
          newFollowOnStudyReportDueDateMomentBusiness.valueOf() >
          storeData.current.followOnStudyReportDueDate?.valueOf()
        ) {
          // addTimezoneToMoment only returns null for an invalid moment; newFollowOnStudyReportDueDateMomentBusiness is always a valid moment built from businessDaysAdd.
          newFollowOnStudyReportDueDateMomentBusiness = addTimezoneToMoment({
            dateMoment: newFollowOnStudyReportDueDateMomentBusiness,
          })!.startOf('days');
          _.assign(setStateObject, {
            followOnStudyReportDueDate: newFollowOnStudyReportDueDateMomentBusiness,
          });
        } else {
          _.assign(setStateObject, {
            followOnStudyReportDueDate: storeData.current.followOnStudyReportDueDate
              ? convertToMoment({ isoDate: storeData.current.followOnStudyReportDueDate })
              : undefined,
          });
        }
      }

      setState(setStateObject);

      return;
    }

    if (name === 'followOnStudyReportDueDate') {
      setState({
        followOnStudyReportDueDate: value || undefined,
        listError: {},
      });
      return;
    }

    if (name === 'studyType') {
      const setStateObject: any = {
        studyType: value || undefined,
        listError: {},
        unitTime: 'day',
        followOnStudyDuration: '3',
        ...(storeData.current.unitTime === 'day'
          ? { duration: storeData.current.duration }
          : { duration: '3' }),
      };

      if (value === FOLLOW_ON_STUDY_TYPE) {
        Object.assign(setStateObject, {
          primaryStudyType: PRIMARY_STUDY_TYPE_LIST[0].value,
          duration: '1',
          followOnStudyStudyType: STUDY_OPTIONS.find((x) => x.value === STUDY_TYPES.MCT_PEEK)
            ?.value,
          followOnStudyDuration: '30',
        });

        const newStopMomentBusiness = dayjs(data?.start).add(Number(state.duration || 0), 'day');
        const newFollowOnStopMomentBusiness = dayjs(data?.start).add(
          Number(state.duration || 0) + Number(state.followOnStudyDuration || 0),
          'day',
        );
        let newReportDueDateMomentBusiness;
        newReportDueDateMomentBusiness = newStopMomentBusiness.businessDaysAdd(
          newStopMomentBusiness.add(1, 'days').isBusinessDay() ? 3 : 4,
        );
        if (
          storeData.current.reportDueDate &&
          newReportDueDateMomentBusiness.valueOf() > storeData.current.reportDueDate.valueOf()
        ) {
          // addTimezoneToMoment only returns null for an invalid moment; newReportDueDateMomentBusiness is always a valid moment built from businessDaysAdd.
          newReportDueDateMomentBusiness = addTimezoneToMoment({
            dateMoment: newReportDueDateMomentBusiness,
          })!.startOf('days');
          _.assign(setStateObject, { reportDueDate: newReportDueDateMomentBusiness });
        } else {
          _.assign(setStateObject, {
            reportDueDate: storeData.current.reportDueDate
              ? convertToMoment({ isoDate: storeData.current.reportDueDate })
              : undefined,
          });
        }

        let newFollowOnStudyReportDueDateMomentBusiness;
        newFollowOnStudyReportDueDateMomentBusiness = newFollowOnStopMomentBusiness.businessDaysAdd(
          newFollowOnStopMomentBusiness.add(1, 'days').isBusinessDay() ? 5 : 6,
        ); // *: Patient return visit is after 5 businesss day
        if (
          !state.followOnStudyReportDueDate ||
          newFollowOnStudyReportDueDateMomentBusiness.valueOf() >
            (convertToMoment({ isoDate: state.followOnStudyReportDueDate })?.valueOf() ?? 0)
        ) {
          // addTimezoneToMoment only returns null for an invalid moment; newFollowOnStudyReportDueDateMomentBusiness is always a valid moment built from businessDaysAdd.
          newFollowOnStudyReportDueDateMomentBusiness = addTimezoneToMoment({
            dateMoment: newFollowOnStudyReportDueDateMomentBusiness,
          })!.startOf('days');
          storeData.current.followOnStudyReportDueDate =
            newFollowOnStudyReportDueDateMomentBusiness;
          _.assign(setStateObject, {
            followOnStudyReportDueDate: newFollowOnStudyReportDueDateMomentBusiness,
          });
        } else {
          storeData.current.followOnStudyReportDueDate = convertToMoment({
            isoDate: state.followOnStudyReportDueDate,
          });
          _.assign(setStateObject, {
            followOnStudyReportDueDate: convertToMoment({
              isoDate: state.followOnStudyReportDueDate,
            }),
          });
        }
      } else {
        const newStopMomentBusiness = dayjs(data?.start).add(Number(state.duration || 0), 'day');

        let newReportDueDateMomentBusiness;
        newReportDueDateMomentBusiness = newStopMomentBusiness.businessDaysAdd(
          newStopMomentBusiness.add(1, 'days').isBusinessDay() ? 5 : 6,
        );
        if (
          storeData.current.reportDueDate &&
          newReportDueDateMomentBusiness.valueOf() > storeData.current.reportDueDate.valueOf()
        ) {
          // addTimezoneToMoment only returns null for an invalid moment; newReportDueDateMomentBusiness is always a valid moment built from businessDaysAdd.
          newReportDueDateMomentBusiness = addTimezoneToMoment({
            dateMoment: newReportDueDateMomentBusiness,
          })!.startOf('days');
          _.assign(setStateObject, { reportDueDate: newReportDueDateMomentBusiness });
        } else {
          _.assign(setStateObject, {
            reportDueDate: storeData.current.reportDueDate
              ? convertToMoment({ isoDate: storeData.current.reportDueDate })
              : undefined,
          });
        }

        storeData.current.followOnStudyReportDueDate = convertToMoment({
          isoDate: data?.info?.followOnStudy?.reportDueDate,
        });

        _.assign(setStateObject, {
          followOnStudyStudyType: undefined,
          followOnStudyDuration: undefined,
          followOnStudyReportDueDate: undefined,
        });
      }

      setState(setStateObject);
      return;
    }

    if (name === 'primaryStudyType') {
      const setStateObject = {
        [name]: value,
        unitTime: 'day',
      };
      switch (value) {
        case STUDY_TYPES.HOLTER: {
          Object.assign(setStateObject, { duration: 1 });
          break;
        }
        default: {
          Object.assign(setStateObject, { duration: 30 });
          break;
        }
      }
      setState(setStateObject);
      return;
    }

    if (name === 'followOnStudyStudyType') {
      setState({
        followOnStudyStudyType: value || undefined,
        listError: {},
        followOnStudyDuration: value === STUDY_TYPES.HOLTER ? 3 : 30,
        unitTimeFO: 'day',
      });
      return;
    }

    setState({ [name]: value });
  };

  /**
   * Picks which state field holds the patient return visit date. Yields followOnStudyReportDueDate for a follow-on study that is neither completed nor done and has not passed its duration, and reportDueDate in every other case.
   *
   * @returns Name of the state field carrying the return visit date.
   */
  const isFOReportDueDate = () => {
    if (
      isFollowOnStudyType &&
      ![STUDY_STATUS.COMPLETED, STUDY_STATUS.DONE].includes(data.status) &&
      !isEndOfDurationStudy
    ) {
      return 'followOnStudyReportDueDate';
    }

    return 'reportDueDate';
  };

  /**
   * Seeds the edit-study-info drawer from the study being viewed. Resolves the study type to the follow-on type when a Holter or extended Holter carries a follow-on study, derives durations and time units from the pending command or the initial duration, converts both report due dates to moments, snapshots all of it plus the pending command and ring ID into storeData.current and applies a deep clone to state.
   *
   * @returns A promise resolving once the initial state has been applied.
   */
  const initiateState = async () => {
    setState({ isInitiating: true });
    const {
      info,
      stop,
      start,
      initDuration,
      initialDurationOfStudy,
      reportDueDate,
      studyType,
      pendingCommand,
      studySetting,
      linkedStudies,
    } = data;
    const { ringId } = studySetting;
    const { studyType: studyTypeTemp } = pendingCommand?.info?.studySetting || {};

    const isFollowOnStudyType = !_.isEmpty(info?.followOnStudy);

    storeData.current = {
      duration: getTimeDuration(
        pendingCommand?.info?.studySetting?.duration,
        initDuration,
        start,
        stop,
      ),
      unitTime: getUnitTime(pendingCommand?.info?.studySetting?.duration, initDuration),
      studyType: isFollowOnStudyType ? FOLLOW_ON_STUDY_TYPE : studyTypeTemp || studyType,
      primaryStudyType: isFollowOnStudyType ? (studyTypeTemp ?? studyType) : null,
      followOnStudyStudyType:
        linkedStudies?.length > 0 ? linkedStudies[1].studyType : info?.followOnStudy?.studyType,
      followOnStudyDuration: getTimeDuration(info.followOnStudy?.duration, 0),
      unitTimeFO: getUnitTime(info.followOnStudy?.duration, initialDurationOfStudy),
      reportDueDate: convertToMoment({ isoDate: reportDueDate }),
      followOnStudyReportDueDate: convertToMoment({
        isoDate: dayjs.utc(info?.followOnStudy?.reportDueDate, 'YYYY-MM-DD'),
      }),
      pendingCommand,
      ringId,
    };

    setState({
      ..._.cloneDeep(storeData.current),
      listError: {},
      isInitiating: false,
    });
  };

  /**
   * Closes the edit-study-info drawer. Calls the onClose prop with the drawer key passed into the hook.
   *
   * @returns Nothing.
   */
  const handleOnClose = () => {
    if (onClose && drawerKey) {
      onClose(drawerKey);
    }
  };

  /**
   * Collects the field errors of the study information form. Checks the primary and follow-on durations with checkStudyDuration against study type, time unit and high sampling rate, requires a patient return visit unless device assignment is pending or the study is Bioflux Direct, demands a follow-on study type, caps ringId at six characters, and rejects MCT study types the Biotres device or its firmware cannot run.
   *
   * @returns Map of field name to error message, empty when valid.
   */
  const validateData = () => {
    const {
      duration,
      unitTime,
      unitTimeFO,
      reportDueDate,
      studyType,
      followOnStudyDuration,
      followOnStudyStudyType,
      followOnStudyReportDueDate,
    } = state;
    const { device, info, status, ecgDetail, start } = data;

    const listError = {};
    const isHighSamplingRate = checkUsingHighSamplingRateStudy(ecgDetail);

    if (![STUDY_STATUS.COMPLETED, STUDY_STATUS.DONE].includes(status)) {
      const isUnitDays = unitTime === 'day';
      const needCheckedStudyType = isFollowOnStudyType ? state.primaryStudyType : state.studyType;
      const durationError = checkStudyDuration({
        studyDuration: (duration || 0) * (isUnitDays ? 1440 : 60),
        studyType: needCheckedStudyType,
        unitTime,
        isHighSamplingRate,
        startTime: start,
        isDisabledEdit,
      });
      if (durationError) {
        _.assign(listError, { duration: durationError });
      }
    }

    const { isDeviceAssignmentPending, isBiofluxDirect } = data;
    const reportDueDateKey = isFOReportDueDate();
    if (_.isEmpty(info?.followOnStudy) && reportDueDateKey === 'reportDueDate') {
      if (!isDeviceAssignmentPending && !isBiofluxDirect) {
        if (_.isEmpty(reportDueDate)) {
          _.assign(listError, { reportDueDate: 'Invalid value' });
        }
      }
    } else {
      if (studyType === FOLLOW_ON_STUDY_TYPE) {
        const isUnitDaysFO = unitTimeFO === 'day';
        const durationFOError = checkStudyDuration({
          studyDuration: (followOnStudyDuration || 0) * (isUnitDaysFO ? 1440 : 60),
          studyType: followOnStudyStudyType,
          unitTime: unitTimeFO,
          isHighSamplingRate,
        });
        const isDisabledFODuration = !studyOngoing || hasDevicePendingCommand;
        if (durationFOError && !isDisabledFODuration) {
          _.assign(listError, {
            followOnStudyDuration: durationFOError,
          });
        }
      }

      if (!isDeviceAssignmentPending && !isBiofluxDirect) {
        if (_.isEmpty(followOnStudyReportDueDate) && isFollowOnStudyType) {
          _.assign(listError, { followOnStudyReportDueDate: 'Invalid value' });
        }

        if (state.ringId && state.ringId.length > 6) {
          _.assign(listError, { ringId: 'Invalid value' });
        }
      }
    }

    if (isFollowOnStudyType) {
      const isUnitDaysFO = unitTimeFO === 'day';
      const followOnDurationError = state.followOnStudyStudyType
        ? checkStudyDuration({
            studyDuration: (followOnStudyDuration || 0) * (isUnitDaysFO ? 1440 : 60),
            studyType: state.followOnStudyStudyType,
            unitTime: unitTimeFO,
            isHighSamplingRate,
          })
        : 'Invalid value';

      if (followOnDurationError) {
        _.assign(listError, { followOnStudyDuration: followOnDurationError });
      }

      if (_.isEmpty(state.followOnStudyStudyType)) {
        _.assign(listError, { followOnStudyStudyType: 'Please select follow-on study type' });
      }
    }

    if (!isCompletedStudy) {
      if (
        device.model === DEVICE.BIOTRES &&
        ![STUDY_TYPES.HOLTER, STUDY_TYPES.EXTENDED_HOLTER].includes(studyType ?? '')
      ) {
        const isBiotresPro = device.model === DEVICE.BIOTRES_PRO;
        if (!device.canRunBiotresMCT && !isBiotresPro && data.facility.status !== 'Test') {
          _.assign(listError, {
            studyType: 'Feature not available.',
          });
        } else if (device.lastSync.schema <= VERSION_CAN_RUN_MCT) {
          _.assign(listError, {
            studyType: "Please update the latest device's firmware version to run this study type.",
          });
        }
      }
    }

    return listError;
  };

  /**
   * Guards saving the study information form. Stops with a no-changes message when checkIsEditting finds nothing edited, writes the duration, return visit and study type errors to state when validateData reports any, and only then opens the confirmation modal.
   *
   * @returns Nothing.
   */
  const handleClickSave = () => {
    const isEditting = checkIsEditting();
    if (!isEditting) {
      showMessageInfo({ content: t`No changes were made.` });
      return;
    }

    const listError = validateData();
    if (!_.isEmpty(listError)) {
      setState({ listError });
      return;
    }
    setState({ isOpenConfirmModal: true });
  };

  /**
   * Persists the edited study settings. Submits the payload built by convertPayloadStudySetting, then closes the confirm modal, toasts success and refreshes the study detail; a pending-command error gets its own retry message while other failures toast a generic error, and the saving flag is always cleared.
   *
   * @returns A promise resolving once the update attempt has finished.
   */
  const onSave = async () => {
    setState({ isSaving: true });
    try {
      await handleUpdateStudySetting(convertPayloadStudySetting(state, data, isDisabledEdit));
      setState({ isOpenConfirmModal: false });
      showMessageSuccess({ content: t`Study information updated successfully.` });
      if (updateStudyDetail) {
        updateStudyDetail();
      }
    } catch (error) {
      console.error('Failed to update study info: ', error);
      if (
        (error instanceof Error && error.message === PENDING_COMMAND_EXISTS_MESSAGE) ||
        _.includes(error as any, PENDING_COMMAND_EXISTS_MESSAGE)
      ) {
        showMessageError({
          content: t`This study is being updated or stopped. Please try again in a moment.`,
        });
      } else {
        showMessageError({ content: t`Failed to update study information.` });
      }
    } finally {
      setState({ isSaving: false });
    }
  };

  useImperativeHandle(ref, () => ({
    handleCloseModals: () => {
      setState({
        isOpenConfirmModal: false,
        isSaving: false,
      });
    },
  }));

  useEffect(() => {
    if (open) {
      initiateState();
    }
  }, [open]);

  /**
   * Holds the option list offered by the study type select. Memoized to the VirtuOx non-numbered options for iCOR devices, the full study options when the study has linked studies and no follow-on study, and the non-numbered options otherwise.
   *
   * @returns The study type options for the select.
   */
  const studyTypeOptions = useMemo(() => {
    const isIcorDevice = checkIcorDevice(data.device);
    if (isIcorDevice) {
      return VIRTU_OX_NON_NUMBER_STUDY_TYPE_OPTIONS;
    }

    return data?.linkedStudies?.length > 0 && _.isEmpty(data?.info?.followOnStudy)
      ? STUDY_OPTIONS
      : NON_NUMBER_STUDY_TYPE_OPTIONS;
  }, [data, state.studyType, state.duration]);

  return {
    state,
    setState,
    hasDevicePendingCommand,
    studyOngoing,
    draftOrStartingStudy,
    isCompletedStudy,
    isFollowOnStudyType,
    isFirstHolterStudy,
    isSecondHolterStudy,
    isEndOfDurationStudy,
    reportDueDateMessage,
    isDisabledEdit,
    studyTypeOptions,
    isFOReportDueDate,
    handleChange,
    handleClickSave,
    handleOnClose,
    onSave,
  };
};
